import { useState, useEffect } from "react";
import axios from "axios";
import { FiEdit } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import InstitutionNavbar from "../components/instituteNavbar";
import Footer from "../components/Footer";
import "../styles/GlobalStyles.css";

// Move constants outside to keep them stable and valid for usage in hooks without re-creation
const qualificationOptions = [
  'NTT (Nursery Teacher Training)',
  'D.El.Ed (Diploma in Elementary Education)',
  'B.Ed (Bachelor of Education)',
  'Integrated Teacher Education Program (ITEP)',
  'Teacher Eligibility Tests (TET/CTET etc.)',
  'Post-Graduation (MA/MSc/MCom)',
  'PhD',
  'NET / SET',
  'Any'
];

const roleTeachingOptions = ['Teacher', 'Assistant Teacher', 'Head Teacher', 'Special Educator'];
const roleNonTeachingOptions = ['Academic Counsellor', 'Academic Coordinator', 'Centre Head/Coordinator', 'Telecaller', 'Female Attendant', 'Accounts Executive', 'School Admin/Office Manager', 'Librarian', 'Lab Assistant'];
const categories = [{ id: "Teaching", name: "Teaching" }, { id: "Non Teaching", name: "Non Teaching" }];
const boardOptions = ['CBSE', 'ICSE', 'Other'];

const subjectOptions = ['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Library'];
const employmentTypes = ['Full time', 'Part time', 'Internship'];
const noticePeriods = [15, 30, 45, 60, 75, 90];
// Removed unused domainOptions

const PostJob = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get("id");
  const mode = searchParams.get("mode");

  const [currentStep, setCurrentStep] = useState(1);

  const [institutionDetails, setInstitutionDetails] = useState({
    name: "",
    type: "",
    address: {}
  });


  const [showCustomSubject, setShowCustomSubject] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    address: {
      street: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      area: ""
    },
    category: "",
    domain: "",
    role: "",
    qualification: "",
    employment_type: "",
    min_experience: "",
    max_experience: "",
    description: "",
    requirements: "",
    prefered_qualifications: [],
    responsibilities: "",
    benefits: "",
    contact_email: "",
    contact_phone: "",
    posted_date: "",
    closing_date: "",
    status: "open",
    hiring_manager: {
      name: "",
      email: "",
      department: ""
    },
    recruiters: [{ name: "", email: "" }],
    vacancies: "",
    is_active: true,
    allow_multiple_applications: false,
    notice_period: "",
    salary_range: {
      lakh: "",
      thousand: "",
      currency: "INR",
      is_visible_to_applicants: true
    }
  });

  const [isEditable, setIsEditable] = useState(mode === "view" ? false : true);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Fetch Institution Details (Logged In User)
  useEffect(() => {
    const fetchInstitutionProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        // NOTE: Adjust this URL to your actual endpoint that returns the logged-in institution's profile
        // Updated endpoint to correct profile route
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/getInstProfile`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Response structure: { Institute: { name, type, ... }, Credits: ... }
        if (response.data && response.data.Institute) {
          const instData = response.data.Institute;
          setInstitutionDetails({
            name: instData.name,
            type: instData.type,
            address: instData.address || {}
          });

          // Check for profile completeness
          const { name, type, address } = instData;
          const isBengaluru = address?.city?.toLowerCase().includes('bengaluru') || address?.city?.toLowerCase().includes('bangalore');

          const isComplete = name && type && address &&
            address.street &&
            address.city &&
            address.state &&
            address.postal_code &&
            address.country &&
            (!isBengaluru || address.area);

          if (!isComplete) {
            // setShowProfileIncompleteModal(true); // Unused
          }

          // Auto-populate address if it's a new job (no jobId)
          if (!jobId) {
            setFormData(prev => ({
              ...prev,
              address: {
                street: instData.address?.street || "",
                city: instData.address?.city || "",
                state: instData.address?.state || "",
                postal_code: instData.address?.postal_code || "",
                country: instData.address?.country || "",
                area: instData.address?.area || ""
              },
              domain: instData.type || ""
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch institution profile", err);

      }
    };
    fetchInstitutionProfile();
  }, [jobId]);
  // Constants moved outside component

  // 2. Fetch Job Details (If Editing)
  useEffect(() => {
    if (jobId) {
      const fetchJob = async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getJob/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          const job = response.data.data;

          if (!job) return;

          const formatDate = (dateString) => dateString ? new Date(dateString).toISOString().split('T')[0] : "";

          setFormData(prev => ({
            ...prev,
            ...job,
            role: job.role || job.role_teaching || job.role_non_teaching || "", // Correctly populate role for Edit Mode
            // Fallback to existing domain logic or institution type if available
            domain: job.domain || prev.domain || institutionDetails.type || "",
            address: job.address || { street: "", city: "", state: "", postal_code: "", country: "", area: "" },
            requirements: Array.isArray(job.requirements) ? job.requirements.join(", ") : job.requirements,
            prefered_qualifications: Array.isArray(job.prefered_qualifications) ? job.prefered_qualifications : [], // Keep as array
            responsibilities: Array.isArray(job.responsibilities) ? job.responsibilities.join(", ") : job.responsibilities,
            benefits: Array.isArray(job.benefits) ? job.benefits.join(", ") : job.benefits,
            posted_date: mode === 'repost' ? "" : formatDate(job.posted_date),
            closing_date: mode === 'repost' ? "" : formatDate(job.closing_date),
            hiring_manager: job.hiring_manager || { name: "", email: "", department: "" },
            recruiters: job.recruiters?.length > 0 ? job.recruiters : [{ name: "", email: "" }],
            salary_range: {
              lakh: job.salary_range?.lakh || "",
              thousand: job.salary_range?.thousand || "",
              currency: job.salary_range?.currency || "INR",
              is_visible_to_applicants: job.salary_range?.is_visible_to_applicants !== undefined ? job.salary_range.is_visible_to_applicants : true
            }
          }));

          // LOCK IF CLOSED (unless reposting)
          if (mode === 'repost') {
            setIsEditable(true);
          } else if (job.closing_date && new Date(job.closing_date) < new Date()) {
            setIsEditable(false);
          }

        } catch (err) {
          console.error("Failed to fetch job", err);
        }
      };
      fetchJob();
    }
  }, [jobId, institutionDetails.type, mode]);

  // Sync custom subject state based on fetched data
  useEffect(() => {
    if (formData.subject && !subjectOptions.includes(formData.subject)) {
      setShowCustomSubject(true);
    }
    // Do not force false here; let onChange handle strict "false" setting when a valid option is picked.
    // This allows "Others" (which sets subject="") to keep showCustomSubject=true.
  }, [formData.subject]);

  // Ensure formData.domain is set if institutionDetails has it (vital for validation)
  useEffect(() => {
    if (institutionDetails.type && !formData.domain) {
      setFormData(prev => ({ ...prev, domain: institutionDetails.type }));
    }
  }, [institutionDetails.type, formData.domain]);




  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested fields
    if (name.includes(".")) {
      const keys = name.split(".");
      if (keys[0] === "recruiters") {
        const [, index, field] = keys;
        const newRecruiters = [...formData.recruiters];
        newRecruiters[index][field] = value;
        setFormData((prev) => ({ ...prev, recruiters: newRecruiters }));
      } else {
        const [group, key] = keys;
        setFormData((prev) => ({
          ...prev,
          [group]: { ...prev[group], [key]: type === "checkbox" ? checked : value }
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };



  const handleNextStep = (e) => {
    e.preventDefault();
    if (!formData.domain) {
      toast.warning("Domain is missing. Please ensure your institution profile is complete.");
      return;
    }
    if (!formData.category) {
      toast.warning("Please select a Category (Teaching or Non Teaching).");
      return;
    }
    if (formData.category === "Teaching" && !formData.role) {
      toast.warning("Please select a Role for Teaching jobs.");
      return;
    }
    setCurrentStep(2);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mandate Closing Date for Repost
    if (mode === 'repost' && !formData.closing_date) {
      toast.warning("Please update closing date");
      return;
    }

    // Validate Closing Date (if present)
    if (formData.closing_date) {
      const selectedDate = new Date(formData.closing_date);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (selectedDate <= now) {
        toast.warning("Closing date must be in the future (tomorrow or later).");
        return;
      }
    }

    // Client-side validation for required fields
    if (!formData.title || !formData.title.trim()) {
      toast.warning("Job title is required.");
      return;
    }

    if (!formData.contact_email || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.contact_email)) {
      toast.warning("A valid contact email is required.");
      return;
    }

    if (!formData.contact_phone || !/^\+?[1-9]\d{6,14}$/.test(formData.contact_phone)) {
      toast.warning("A valid contact phone number is required (7-15 digits).");
      return;
    }

    if (formData.min_experience && formData.max_experience && Number(formData.min_experience) > Number(formData.max_experience)) {
      toast.warning("Minimum experience cannot exceed maximum experience.");
      return;
    }

    if (formData.vacancies && Number(formData.vacancies) < 1) {
      toast.warning("Vacancies must be at least 1.");
      return;
    }

    const formattedData = {
      ...formData,
      requirements: typeof formData.requirements === 'string' ? formData.requirements.split(",").map((item) => item.trim()).filter(Boolean) : formData.requirements,
      prefered_qualifications: Array.isArray(formData.prefered_qualifications) ? formData.prefered_qualifications : [],
      responsibilities: typeof formData.responsibilities === 'string' ? formData.responsibilities.split(",").map((item) => item.trim()).filter(Boolean) : formData.responsibilities,
      benefits: typeof formData.benefits === 'string' ? formData.benefits.split(",").map((item) => item.trim()).filter(Boolean) : formData.benefits
    };

    // Remove posted_date from payload to prevent overwriting original date
    delete formattedData.posted_date;

    if (mode === 'repost') {
      formattedData.repost_of_id = jobId;
    }

    try {
      const accessToken = localStorage.getItem("token");
      if (!accessToken) {
        toast.error("Missing authentication token. Please log in again.");
        return;
      }

      setIsSubmitting(true);

      if (jobId && mode !== 'repost') {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/institutionfilter/updateJob/${jobId}`, formattedData, {
          headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
          withCredentials: true,
        });
        toast.success("Job updated successfully!");
        setIsEditable(false);
        navigate("/InstitutionHomepage");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/institutionfilter/addNewJob`, formattedData, {
          headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
          withCredentials: true,
        });
        toast.success("Job posted successfully!");
        navigate("/InstitutionHomepage");
      }

    } catch (error) {
      console.error("Error saving job:", error.response?.data || error.message);
      if (error.response?.status === 403 && error.response?.data?.message?.includes("Insufficient credits")) {
        setShowCreditModal(true);
      } else {
        toast.error(error.response?.data?.message || "Failed to save job.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleOptions = formData.category === 'Non Teaching'
    ? roleNonTeachingOptions
    : (formData.category === 'Teaching' ? roleTeachingOptions : []);

  // Removed internal definitions of static constants


  return (
    <>
      <InstitutionNavbar />
      <div
        className="page-container form-card mt-[120px] md:mt-[120px]"
      >



        {/* Header */}
        <div className="form-header">
          <h2 className="page-title mb-0">
            {mode === 'repost' ? "Repost Job" : (jobId ? (isEditable ? "Edit Job Posting" : "Job Details") : "Post a New Job")}
          </h2>
          <div
            className="text-gray-400 transition-colors cursor-pointer hover:text-blue-600"
            onClick={() => setIsEditable((prev) => !prev)}
            title={isEditable ? "Disable Editing" : "Enable Editing"}
          >
            <FiEdit size={20} />
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ================= STEP 1 ================= */}
          {currentStep === 1 && (
            <div className="form-grid-2 animate-fadeIn">

              {/* 1. Hiring For (Read Only - Institution Name) */}
              <div className="form-group-full">
                <label className="profile-label">Hiring For</label>
                <input
                  type="text"
                  value={institutionDetails.name}
                  readOnly
                  className="profile-input bg-gray-100 cursor-not-allowed"
                />
              </div>

              {/* 2. Candidates preferably working in (Domain) - Auto-filled from Profile */}
              <div className="form-group-full">
                <label className="profile-label">
                  Candidates preferably working in
                </label>
                <input
                  type="text"
                  name="domain"
                  value={institutionDetails.type || formData.domain}
                  readOnly
                  className="profile-input bg-gray-100 cursor-not-allowed"
                  title="This domain is linked to your institution profile."
                />
              </div>

              {/* 4. Category (Teaching/Non-Teaching) */}
              <div className="flex flex-col">
                <label className="profile-label">Category</label>
                <select
                  name="category"
                  disabled={!isEditable}
                  value={formData.category}
                  onChange={handleChange}
                  className="profile-input"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* 5. Role Dropdown */}
              <div className="flex flex-col">
                <label className="profile-label">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="profile-input"
                >
                  <option value="">Select Role</option>
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              {/* Next Button */}
              {isEditable && (
                <div className="md:col-span-2 mt-4">
                  <button
                    onClick={handleNextStep}
                    className="btn-next"
                  >
                    Next &rarr;
                  </button>
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="animate-fadeIn space-y-8">

              {/* Job Title Section */}
              <div className="section-card">
                <label className="profile-label">Suggested Job Title</label>
                <input
                  name="title"
                  disabled={!isEditable || !!jobId}
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Senior Math Teacher"
                  className={`profile-input ${jobId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              </div>

              {/* Attributes Grid */}
              <div className="attributes-grid">

                {/* Min/Max Experience */}
                <div className="form-group">
                  <label className="profile-label">Work Experience (Years)</label>
                  <div className="flex gap-2">
                    <input type="number" name="min_experience" placeholder="Min" disabled={!isEditable} value={formData.min_experience} onChange={handleChange} className="profile-input" min="0" />
                    <input type="number" name="max_experience" placeholder="Max" disabled={!isEditable} value={formData.max_experience} onChange={handleChange} className="profile-input" min="0" />
                  </div>
                </div>

                {/* Salary */}
                <div className="form-group-full">
                  <label className="profile-label">Salary (₹) <span className="text-gray-400 text-sm font-normal normal-case ml-1">Per Annum</span></label>
                  <div className="flex gap-4 items-center">
                    <div className="flex-1">
                      <input type="number" name="salary_range.lakh" disabled={!isEditable} value={formData.salary_range?.lakh || ''} onChange={handleChange} className="profile-input" placeholder="Lakhs" min="0" />
                      <span className="text-xs text-gray-500 mt-1 block">Lakhs</span>
                    </div>
                    <div className="flex-1">
                      <input type="number" name="salary_range.thousand" disabled={!isEditable} value={formData.salary_range?.thousand || ''} onChange={handleChange} className="profile-input" placeholder="Thousands" min="0" />
                      <span className="text-xs text-gray-500 mt-1 block">Thousands</span>
                    </div>
                  </div>
                </div>

                {/* Employment Type */}
                <div className="form-group">
                  <label className="profile-label">Employment Type</label>
                  <select name="employment_type" disabled={!isEditable} value={formData.employment_type} onChange={handleChange} className="profile-input">
                    <option value="">Select Type</option>
                    {employmentTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>

                {/* Closing Date */}
                <div className="form-group">
                  <label className="profile-label">Closing Date</label>
                  <input
                    type="date"
                    name="closing_date"
                    disabled={!isEditable || (!!jobId && mode !== 'repost')}
                    value={formData.closing_date}
                    onChange={handleChange}
                    className={`profile-input ${(jobId && mode !== 'repost') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    min={new Date().toISOString().split('T')[0]} // Optional: Prevent past dates
                  />
                </div>

                {/* Notice Period */}
                <div className="form-group">
                  <label className="profile-label">Max Notice Period</label>
                  <select name="notice_period" disabled={!isEditable} value={formData.notice_period} onChange={handleChange} className="profile-input">
                    <option value="">Select Days</option>
                    {noticePeriods.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>

                {/* Board */}
                <div className="form-group">
                  <label className="profile-label">Education Board</label>
                  <select name="board" disabled={!isEditable} value={formData.board} onChange={handleChange} className="profile-input">
                    <option value="">Select Board</option>
                    {boardOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                {/* Qualification */}
                <div className="form-group">
                  <label className="profile-label">Qualification</label>
                  <ReactSelect
                    isMulti
                    name="prefered_qualifications"
                    options={qualificationOptions.map(opt => ({ value: opt, label: opt }))}
                    value={
                      // Map the array of strings to objects {value, label} for React Select
                      Array.isArray(formData.prefered_qualifications)
                        ? formData.prefered_qualifications.map(q => ({ value: q, label: q }))
                        : []
                    }
                    onChange={(selectedOptions) => {
                      // Map selected objects back to array of strings
                      const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                      setFormData(prev => ({ ...prev, prefered_qualifications: values }));
                    }}
                    isDisabled={!isEditable}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '45px',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                        '&:hover': { borderColor: '#3b82f6' }
                      })
                    }}
                    placeholder="Select Qualifications..."
                  />
                </div>




                {/* Subject */}
                {formData.category !== "Non Teaching" && (
                  <div className="form-group">
                    <label className="profile-label">Subject</label>
                    <select
                      name="subject"
                      disabled={!isEditable}
                      value={subjectOptions.includes(formData.subject) ? formData.subject : "Others"}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "Others") {

                          setFormData(prev => ({ ...prev, subject: "" }));
                          setShowCustomSubject(true);
                        } else {
                          setFormData(prev => ({ ...prev, subject: val }));
                          setShowCustomSubject(false);
                        }
                      }}
                      className="profile-input"
                    >
                      <option value="">Select Subject</option>
                      {subjectOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      <option value="Others">Others</option>
                    </select>

                    {(showCustomSubject || (formData.subject && !subjectOptions.includes(formData.subject))) ? (
                      <input
                        type="text"
                        placeholder="Enter subject"
                        value={formData.subject}
                        onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                        className="profile-input mt-2"
                      />
                    ) : null}

                  </div>
                )}

                <div className="form-group">
                  <label className="profile-label">Vacancies</label>
                  <input
                    type="number"
                    name="vacancies"
                    disabled={!isEditable}
                    value={formData.vacancies}
                    onChange={handleChange}
                    className="profile-input"
                    placeholder="Enter Job Openings"
                    min="1"
                  />
                </div>

              </div>



              {/* Description Section */}
              <div className="space-y-6">
                <div className="flex gap-4">
                  {/* Contact Email */}
                  <div className="form-group flex-1">
                    <label className="text-label-caps">Contact Email</label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email || ''}
                      onChange={handleChange}
                      disabled={!isEditable}
                      className="profile-input"
                      placeholder="Enter email to receive applications..."
                    />
                  </div>

                  {/* Contact Phone */}
                  <div className="form-group flex-1">
                    <label className="text-label-caps">Contact Phone</label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone || ''}
                      onChange={handleChange}
                      disabled={!isEditable}
                      className="profile-input"
                      placeholder="Enter phone number..."
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div className="form-group">
                  <label className="profile-label">Skills Required <span className="text-gray-400 font-normal italic">Optional</span></label>
                  <input name="requirements" value={formData.requirements} onChange={handleChange} className="profile-input" placeholder="e.g. Communication, Java, Class Management..." />
                </div>

                {/* Responsibilities */}
                <div className="form-group">
                  <label className="profile-label">Responsibilities <span className="text-gray-400 font-normal italic">Optional</span></label>
                  <textarea name="responsibilities" value={formData.responsibilities} onChange={handleChange} className="profile-input" rows="3" placeholder="e.g. Conduct classes, Assess students, Maintain records... (Comma separated)" />
                </div>

                {/* Benefits */}
                <div className="form-group">
                  <label className="profile-label">Benefits <span className="text-gray-400 font-normal italic">Optional</span></label>
                  <textarea name="benefits" value={formData.benefits} onChange={handleChange} className="profile-input" rows="2" placeholder="e.g. Health Insurance, Transport, PF... (Comma separated)" />
                </div>

                {/* Description */}
                <div className="form-group">
                  <label className="profile-label flex items-center gap-2">
                    Job Description
                  </label>
                  <div className="description-card">
                    <textarea name="description" rows="4" disabled={!isEditable} value={formData.description} onChange={handleChange} className="profile-input min-h-[100px]" placeholder="Describe the role..."></textarea>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {isEditable && (
                <div className="action-buttons-container">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="btn-back-outline"
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                  <button type="submit" className="btn-primary-pill" disabled={isSubmitting}>
                    {isSubmitting ? "Posting..." : (jobId && mode !== 'repost' ? "Save Job" : "Post Job")}
                  </button>
                </div>
              )}
            </div>
          )}

        </form>
      </div>


      {/* Insufficient Credits Modal */}
      {
        showCreditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md transform transition-all scale-100 border border-gray-100">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Insufficient Balance</h3>
                <p className="text-gray-500 mb-8">
                  You do not have enough credits to post this job. Please purchase more credits to continue.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      setShowCreditModal(false);
                      // navigate("/pricing"); // Uncomment if you have a pricing route
                      window.location.href = "/wallet"; // Or use this
                    }}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-3 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm transition-colors"
                  >
                    Add Credits
                  </button>
                  <button
                    onClick={() => setShowCreditModal(false)}
                    className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <Footer />
    </>
  );
};

export default PostJob;